import { useEffect, useRef, useState, useCallback, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { LanguageProvider } from "./lib/LanguageContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { getAuthHeaders } from "./lib/api";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { RouteProgressBar } from "./components/RouteProgressBar";
import "./App.css";

// Eagerly loaded - critical for initial render
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import Register from "./pages/Register";
import PendingApproval from "./pages/PendingApproval";

// Lazy loaded - loaded on demand for better performance
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Communities = lazy(() => import("./pages/Communities"));
const Messages = lazy(() => import("./pages/Messages"));
const Settings = lazy(() => import("./pages/Settings"));
const TravelFriends = lazy(() => import("./pages/TravelFriends"));
const CommunityChat = lazy(() => import("./pages/CommunityChat"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ChatWidget = lazy(() => import("./components/ChatWidget"));
// AdminQuickActions removed per user request
const LoginTest = lazy(() => import("./pages/LoginTest"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const Notifications = lazy(() => import("./pages/Notifications"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ThemedCommunity = lazy(() => import("./pages/ThemedCommunity"));
const MyBusiness = lazy(() => import("./pages/MyBusiness"));
const SqawkerSkills = lazy(() => import("./pages/SqawkerSkills"));
const CreditsSuccess = lazy(() => import("./pages/CreditsSuccess"));
const CallPage = lazy(() => import("./pages/CallPage"));
const LiveStreamPage = lazy(() => import("./pages/LiveStreamPage"));
const Referrals = lazy(() => import("./pages/Referrals"));
const HashtagPage = lazy(() => import("./pages/HashtagPage"));
const TrendingPage = lazy(() => import("./pages/TrendingPage"));
const Directory = lazy(() => import("./pages/Directory"));
const BusinessHub = lazy(() => import("./pages/BusinessHub"));
const Sports247 = lazy(() => import("./pages/Sports247"));
const SqawkFriends = lazy(() => import("./pages/SqawkFriends"));
const BusinessFeed = lazy(() => import("./pages/BusinessFeed"));
const MySchedule = lazy(() => import("./pages/MySchedule"));
const BookAppointment = lazy(() => import("./pages/BookAppointment"));
const EventCommunity = lazy(() => import("./pages/EventCommunity"));
const DiscoverEvents = lazy(() => import("./pages/DiscoverEvents"));
const ContactForm = lazy(() => import("./pages/ContactForm"));
const SqawkNonProfits = lazy(() => import("./pages/SqawkNonProfits"));
const NpoEventDetail = lazy(() => import("./pages/NpoEventDetail"));
import TermsOfService from "./pages/TermsOfService";
import GlobalMobileNav from "./components/GlobalMobileNav";
import { InstallPrompt } from "./components/common/InstallPrompt";

// Loading spinner for lazy-loaded components - sQawk branded
const SQAWK_LOGO = "https://customer-assets.emergentagent.com/job_09def4a9-eaab-440b-be7c-c94ecd8ec27b/artifacts/yhrzun8q_ChatGPT%20Image%20Jan%2027%2C%202026%2C%2007_59_06%20PM.png";
const SQAWK_Q_BIRD = "https://customer-assets.emergentagent.com/job_983aea55-2298-45ce-a386-3748c8794840/artifacts/0wxc6apa_Q_logo_transparent.png";
const PageLoader = () => (
  <div className="min-h-screen bg-[#020617]" data-testid="page-loader">
    {/* Minimal skeleton — keeps screen dark instead of flashing a spinner */}
    <div className="animate-pulse pt-16 px-4 max-w-2xl mx-auto space-y-4">
      <div className="h-8 bg-slate-800/50 rounded-lg w-48" />
      <div className="h-4 bg-slate-800/30 rounded w-full" />
      <div className="h-4 bg-slate-800/30 rounded w-3/4" />
      <div className="h-32 bg-slate-800/20 rounded-xl mt-6" />
    </div>
  </div>
);

// Runtime detection of API URL based on current domain
const getBackendUrl = () => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  // Production = relative URLs
  if (hostname === 'sqawk.app' || hostname === 'www.sqawk.app') {
    return '';
  }
  // Use environment variable for deployed environments, fallback to empty for relative URLs
  return process.env.REACT_APP_BACKEND_URL || '';
};
const BACKEND_URL = getBackendUrl();

// Initialize theme mode from localStorage
const initThemeMode = () => {
  const savedMode = localStorage.getItem('sqawk_theme_mode');
  if (savedMode === 'light') {
    document.documentElement.classList.add('light-mode');
  } else {
    document.documentElement.classList.remove('light-mode');
  }
};
// Run on load
if (typeof window !== 'undefined') {
  initThemeMode();
}

// Auth hook - simplified for reliability
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const checkedRef = useRef(false);

  const checkAuth = async () => {
    const token = localStorage.getItem('sqawk_session');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    // Instant PWA resume: use cached user data immediately while API validates
    const cachedUser = localStorage.getItem('sqawk_user');
    if (cachedUser) {
      try {
        const parsed = JSON.parse(cachedUser);
        if (parsed?.user_id) {
          setUser(parsed);
          setLoading(false); // Show dashboard instantly
        }
      } catch {}
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        localStorage.setItem('sqawk_user', JSON.stringify(userData));
      } else if (response.status === 401) {
        // Session expired — clear everything
        setUser(null);
        localStorage.removeItem('sqawk_session');
        localStorage.removeItem('sqawk_user');
      } else {
        // Network/server issue — keep user logged in if we have cached data
        // This prevents logout on temporary network failures (common in PWA)
        const cachedUser = localStorage.getItem('sqawk_user');
        if (cachedUser) {
          try { setUser(JSON.parse(cachedUser)); } catch { setUser(null); }
        } else {
          setUser(null);
        }
      }
    } catch (err) {
      console.error('Auth check error:', err);
      setUser(null);
    }
    setLoading(false);
  };
  
  // Force refresh user data from server (useful after VIP status changes)
  const refreshUser = async () => {
    const token = localStorage.getItem('sqawk_session');
    if (!token) return null;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        // Cache user data for PWA offline resilience
        localStorage.setItem('sqawk_user', JSON.stringify(userData));
        return userData;
      }
    } catch (err) {
      console.error('User refresh error:', err);
    }
    return null;
  };

  const logout = async () => {
    const token = localStorage.getItem('sqawk_session');
    
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
    } catch (err) {
      console.error('Logout error:', err);
    }
    
    localStorage.removeItem('sqawk_session');
    localStorage.removeItem('sqawk_user');
    setUser(null);
  };

  useEffect(() => {
    if (!checkedRef.current) {
      checkedRef.current = true;
      checkAuth();
    }
  }, []);
  
  // Auto-refresh user data every 5 minutes to catch VIP status changes
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(() => {
      refreshUser();
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(interval);
  }, [user]);

  return { user, setUser, loading, logout, checkAuth, refreshUser };
};

// Protected route wrapper
const ProtectedRoute = ({ children, user, loading }) => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      navigate("/", { replace: true });
    }
  }, [user, loading, navigate, location.state]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex flex-col items-center justify-center">
        <img src={SQAWK_Q_BIRD} alt="sQawk" className="w-20 h-20 object-contain animate-spin drop-shadow-[0_0_20px_rgba(59,130,246,0.5)]" />
        <div className="flex gap-1 mt-4">
          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    );
  }

  return children;
};

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  // Disable browser scroll restoration globally on mount
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);
  // Scroll to top on every route change AND initial load
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    });
  }, [pathname]);
  return null;
}

// Main app router
function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setUser, loading, logout } = useAuth();
  const lastCallIdRef = useRef(null);
  const ringtoneAudioRef = useRef(null);
  const audioContextRef = useRef(null);
  const oscillatorRef = useRef(null);
  const ringtoneIntervalRef = useRef(null);

  // Global image error fallback — when any <img> fails to load (e.g. old
  // customer-assets.emergentagent.com URLs from a previous fork that now 403,
  // or example.com/* placeholders), swap to a deterministic Dicebear avatar
  // (for avatar-shaped images) or a soft "no image" SVG placeholder. Prevents
  // broken-image icons across the app without refactoring 189+ render sites.
  useEffect(() => {
    const NO_IMAGE_SVG = "data:image/svg+xml;utf8," + encodeURIComponent(
      "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'><rect width='200' height='200' fill='%231e293b'/><g fill='%2364748b' opacity='0.55'><circle cx='100' cy='80' r='28'/><path d='M50 170c0-30 25-50 50-50s50 20 50 50z'/></g></svg>"
    );
    const handler = (e) => {
      const img = e.target;
      if (!img || img.tagName !== "IMG") return;
      if (img.dataset.fallbackApplied === "1") return;
      img.dataset.fallbackApplied = "1";
      // Heuristic: avatar-shaped = inside Radix avatar root OR small square OR alt mentions profile/user
      const alt = (img.alt || "").toLowerCase();
      const inAvatar = !!img.closest('[data-radix-collection-item], [class*="rounded-full"], [class*="avatar" i]');
      const looksLikeAvatar = inAvatar || /profile|avatar|user|host|attendee/.test(alt);
      if (looksLikeAvatar) {
        const seed = encodeURIComponent(alt || img.dataset.seed || "sqawk");
        img.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${seed}`;
      } else {
        img.src = NO_IMAGE_SVG;
      }
    };
    document.addEventListener("error", handler, true); // capture phase so we get all <img>s
    return () => document.removeEventListener("error", handler, true);
  }, []);

  // Bird squawk ringtone - works better on mobile than Web Audio API
  // Uses a real audio file that's more reliable across devices
  const BIRD_SQUAWK_URL = "https://cdn.pixabay.com/audio/2022/03/15/audio_8f8e1a8389.mp3"; // Bird chirp sound

  // Function to play ringtone - mobile-friendly with actual audio file
  const playRingtone = useCallback(() => {
    // Stop any existing ringtone first
    if (ringtoneAudioRef.current) {
      ringtoneAudioRef.current.pause();
      ringtoneAudioRef.current.currentTime = 0;
    }
    if (ringtoneIntervalRef.current) {
      clearInterval(ringtoneIntervalRef.current);
    }
    if (oscillatorRef.current) {
      try { oscillatorRef.current.stop(); } catch (e) {}
    }

    // Try HTML5 Audio first (works better on mobile)
    const tryAudioElement = () => {
      try {
        if (!ringtoneAudioRef.current) {
          ringtoneAudioRef.current = new Audio(BIRD_SQUAWK_URL);
          ringtoneAudioRef.current.volume = 0.8;
        }
        
        // Play the bird squawk
        const playSound = () => {
          if (ringtoneAudioRef.current) {
            ringtoneAudioRef.current.currentTime = 0;
            ringtoneAudioRef.current.play().catch(e => {
              console.log("Audio play blocked, trying Web Audio API fallback");
              tryWebAudioAPI();
            });
          }
        };
        
        // Play immediately
        playSound();
        
        // Loop every 2 seconds for continuous ringing effect
        ringtoneIntervalRef.current = setInterval(playSound, 2000);
        return true;
      } catch (e) {
        console.log("Audio element failed:", e);
        return false;
      }
    };

    // Fallback to Web Audio API (bird-like squawk sound)
    const tryWebAudioAPI = () => {
      try {
        // Create audio context if not exists
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        
        const audioContext = audioContextRef.current;
        
        // Resume audio context (required for mobile)
        if (audioContext.state === 'suspended') {
          audioContext.resume();
        }
        
        // Function to play bird-like squawk sound
        const playBirdSquawk = () => {
          // Create oscillator for bird-like sound (high pitched, warbling)
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          // Bird squawk frequency pattern (higher pitched, more natural)
          const now = audioContext.currentTime;
          oscillator.frequency.setValueAtTime(800, now);
          oscillator.frequency.exponentialRampToValueAtTime(1200, now + 0.05);
          oscillator.frequency.exponentialRampToValueAtTime(600, now + 0.1);
          oscillator.frequency.exponentialRampToValueAtTime(1000, now + 0.15);
          oscillator.frequency.exponentialRampToValueAtTime(700, now + 0.2);
          oscillator.frequency.exponentialRampToValueAtTime(900, now + 0.25);
          
          // Volume envelope for bird-like attack
          gainNode.gain.setValueAtTime(0.5, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
          
          oscillator.start(now);
          oscillator.stop(now + 0.3);
          
          oscillatorRef.current = oscillator;
        };
        
        // Play squawk pattern: squawk, pause, squawk, pause
        playBirdSquawk();
        setTimeout(playBirdSquawk, 350);
        
        ringtoneIntervalRef.current = setInterval(() => {
          playBirdSquawk();
          setTimeout(playBirdSquawk, 350);
        }, 1500);
        
      } catch (e) {
        console.log("Web Audio API failed:", e);
      }
    };

    // Try audio element first, fallback to Web Audio API
    tryAudioElement();
  }, []);

  // Function to stop ringtone
  const stopRingtone = useCallback(() => {
    // Stop HTML5 Audio
    if (ringtoneAudioRef.current) {
      ringtoneAudioRef.current.pause();
      ringtoneAudioRef.current.currentTime = 0;
    }
    // Clear interval
    if (ringtoneIntervalRef.current) {
      clearInterval(ringtoneIntervalRef.current);
      ringtoneIntervalRef.current = null;
    }
    // Stop oscillator
    if (oscillatorRef.current) {
      try { oscillatorRef.current.stop(); } catch (e) {}
      oscillatorRef.current = null;
    }
  }, []);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      stopRingtone();
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [stopRingtone]);

  // Global incoming call listener - polls every 3 seconds
  const checkForIncomingCalls = useCallback(async () => {
    if (!user?.user_id) return;
    
    // Don't check if already on a call page
    if (location.pathname.startsWith('/call/')) {
      stopRingtone();
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/calls/incoming/check`, {
        headers: getAuthHeaders(),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.has_incoming && data.call && data.call.call_id !== lastCallIdRef.current) {
          lastCallIdRef.current = data.call.call_id;
          
          // Play ringtone
          playRingtone();
          
          // Show toast notification with actions
          toast.custom((t) => (
            <div className="bg-slate-900 border border-emerald-500/50 rounded-xl p-4 shadow-xl max-w-sm animate-pulse">
              <div className="flex items-center gap-3 mb-3">
                <img 
                  src={data.call.caller?.profile_picture || '/default-avatar.png'} 
                  alt="" 
                  className="w-12 h-12 rounded-full object-cover ring-2 ring-emerald-500"
                />
                <div>
                  <p className="text-white font-semibold">{data.call.caller?.name || 'Someone'}</p>
                  <p className="text-emerald-400 text-sm flex items-center gap-1">
                    {data.call.call_type === 'video' ? '📹' : '📞'} Incoming {data.call.call_type} call
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    stopRingtone();
                    toast.dismiss(t);
                    navigate(`/call/${data.call.call_id}`);
                  }}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white py-2 rounded-lg font-medium transition-colors"
                >
                  ✓ Answer
                </button>
                <button
                  onClick={async () => {
                    stopRingtone();
                    toast.dismiss(t);
                    try {
                      await fetch(`${BACKEND_URL}/api/calls/${data.call.call_id}/decline`, {
                        method: 'POST',
                        headers: getAuthHeaders(),
                      });
                    } catch (e) {}
                    lastCallIdRef.current = null;
                  }}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg font-medium transition-colors"
                >
                  ✕ Decline
                </button>
              </div>
            </div>
          ), { duration: 30000, id: `call-${data.call.call_id}` });
        } else if (!data.has_incoming) {
          // No incoming call, stop ringtone if playing
          stopRingtone();
        }
      }
    } catch (error) {
      console.error('Error checking incoming calls:', error);
    }
  }, [user?.user_id, navigate, location.pathname, playRingtone, stopRingtone]);

  useEffect(() => {
    if (!user?.user_id) return;
    
    // Check immediately
    checkForIncomingCalls();
    
    // Poll every 3 seconds
    const interval = setInterval(checkForIncomingCalls, 3000);
    
    return () => {
      clearInterval(interval);
      stopRingtone(); // Stop ringtone when unmounting
    };
  }, [checkForIncomingCalls, user?.user_id, stopRingtone]);

  const [notifications, setNotifications] = useState({ unread_count: 0 });

  // Poll for notifications every 15 seconds
  useEffect(() => {
    if (!user?.user_id) return;
    const fetchNotifications = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/notifications?limit=1`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          const count = data.unread_count || 0;
          setNotifications({ unread_count: count });
          // Update PWA badge count on home screen icon
          if ('setAppBadge' in navigator) {
            if (count > 0) {
              navigator.setAppBadge(count).catch(() => {});
            } else {
              navigator.clearAppBadge().catch(() => {});
            }
          }
        }
      } catch (e) {}
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [user?.user_id]);

  // Code protection — disable right-click, copy, and dev tools on authenticated pages
  useEffect(() => {
    if (!user?.user_id) return; // Only protect for logged-in users
    
    const disableContext = (e) => { e.preventDefault(); };
    const disableCopy = (e) => { e.preventDefault(); };
    const disableKeys = (e) => {
      if (e.key === 'F12' || 
          (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C')) ||
          (e.ctrlKey && e.key === 'u') ||
          (e.ctrlKey && e.key === 'U')) {
        e.preventDefault();
      }
    };
    
    document.addEventListener('contextmenu', disableContext);
    document.addEventListener('copy', disableCopy);
    document.addEventListener('keydown', disableKeys);
    
    return () => {
      document.removeEventListener('contextmenu', disableContext);
      document.removeEventListener('copy', disableCopy);
      document.removeEventListener('keydown', disableKeys);
    };
  }, [user?.user_id]);

  return (
    <>
      <ScrollToTop />
      <RouteProgressBar />
      <GlobalMobileNav user={user} notifications={notifications} onLogout={logout}>
        <Suspense fallback={<PageLoader />}>
          <AnimatePresence mode="popLayout">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
            >
          <Routes>
          <Route path="/login-test" element={<LoginTest />} />
          <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login setUser={setUser} />} />
          <Route path="/register" element={user ? <Navigate to="/dashboard" replace /> : <Register setUser={setUser} />} />
          <Route path="/pending-approval" element={<PendingApproval />} />
          <Route
            path="/"
            element={
              loading ? (
                <div className="min-h-screen bg-[#020617] flex flex-col items-center justify-center">
                  <img src={SQAWK_Q_BIRD} alt="sQawk" className="w-20 h-20 object-contain animate-spin drop-shadow-[0_0_20px_rgba(59,130,246,0.5)]" />
                  <div className="flex gap-1 mt-4">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              ) : user ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <LandingPage user={user} setUser={setUser} navigate={navigate} />
              )
            }
          />
          <Route path="/auth/callback" element={<AuthCallback setUser={setUser} />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute user={user || location.state?.user} loading={loading}>
                <Dashboard user={user || location.state?.user} setUser={setUser} logout={logout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/:username"
            element={
              <Profile currentUser={user || location.state?.user} />
            }
          />
          <Route
            path="/profile"
            element={
              user?.username ? (
                <Navigate to={`/profile/${user.username}`} replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/communities"
            element={
              <ProtectedRoute user={user || location.state?.user} loading={loading}>
              <Communities user={user || location.state?.user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/community/:communityId/chat"
          element={
            <ProtectedRoute user={user || location.state?.user} loading={loading}>
              <CommunityChat user={user || location.state?.user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/search"
          element={
            <ProtectedRoute user={user || location.state?.user} loading={loading}>
              <SearchPage user={user || location.state?.user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute user={user || location.state?.user} loading={loading}>
              <Notifications user={user || location.state?.user} />
            </ProtectedRoute>
          }
        />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/directory" element={<Directory />} />
        <Route
          path="/business-hub"
          element={
            <ProtectedRoute user={user || location.state?.user} loading={loading}>
              <BusinessHub user={user || location.state?.user} />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/business-feed" 
          element={
            <ProtectedRoute user={user || location.state?.user} loading={loading}>
              <BusinessFeed user={user || location.state?.user} />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/sports247" 
          element={
            <ProtectedRoute user={user || location.state?.user} loading={loading}>
              <Sports247 user={user || location.state?.user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/friends"
          element={
            <ProtectedRoute user={user || location.state?.user} loading={loading}>
              <SqawkFriends user={user || location.state?.user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/messages"
          element={
            <ProtectedRoute user={user || location.state?.user} loading={loading}>
              <Messages user={user || location.state?.user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute user={user || location.state?.user} loading={loading}>
              <Settings user={user || location.state?.user} setUser={setUser} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/travel-friends"
          element={
            <ProtectedRoute user={user || location.state?.user} loading={loading}>
              <TravelFriends user={user || location.state?.user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-business"
          element={<Navigate to="/business-hub?tab=my-business" replace />}
        />
        <Route
          path="/my-schedule"
          element={
            <ProtectedRoute user={user || location.state?.user} loading={loading}>
              <MySchedule user={user || location.state?.user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/book/:username"
          element={<BookAppointment user={user || location.state?.user} />}
        />
        <Route
          path="/event/:offering_id"
          element={
            <ProtectedRoute user={user || location.state?.user} loading={loading}>
              <EventCommunity user={user || location.state?.user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/discover-events"
          element={<DiscoverEvents user={user || location.state?.user} />}
        />
        <Route
          path="/sqawker-skills"
          element={<SqawkerSkills />}
        />
        <Route
          path="/call/:callId"
          element={
            <ProtectedRoute user={user || location.state?.user} loading={loading}>
              <CallPage user={user || location.state?.user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/live/:streamId"
          element={
            <ProtectedRoute user={user || location.state?.user} loading={loading}>
              <LiveStreamPage user={user || location.state?.user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/credits/success"
          element={
            <ProtectedRoute user={user || location.state?.user} loading={loading}>
              <CreditsSuccess user={user || location.state?.user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute user={user || location.state?.user} loading={loading}>
              <AdminDashboard currentUser={user || location.state?.user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/community/:slug"
          element={
            <ThemedCommunity user={user || location.state?.user} />
          }
        />
        <Route
          path="/referrals"
          element={
            <ProtectedRoute user={user || location.state?.user} loading={loading}>
              <Referrals user={user || location.state?.user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hashtag/:tag"
          element={
            <ProtectedRoute user={user || location.state?.user} loading={loading}>
              <HashtagPage user={user || location.state?.user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/terms"
          element={<TermsOfService />}
        />
        <Route
          path="/terms-of-service"
          element={<TermsOfService />}
        />
        <Route
          path="/trending"
          element={
            <ProtectedRoute user={user || location.state?.user} loading={loading}>
              <TrendingPage user={user || location.state?.user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/trending/:tag"
          element={
            <ProtectedRoute user={user || location.state?.user} loading={loading}>
              <TrendingPage user={user || location.state?.user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/contact/:username"
          element={<ContactForm />}
        />
        <Route
          path="/nonprofits"
          element={<SqawkNonProfits user={user || location.state?.user} />}
        />
        <Route
          path="/npo-event/:eventId"
          element={<NpoEventDetail />}
        />
        </Routes>
            </motion.div>
          </AnimatePresence>
        </Suspense>
      </GlobalMobileNav>
      
      {user && (
        <Suspense fallback={null}>
          <ChatWidget user={user} notifications={notifications} />
        </Suspense>
      )}
      
      {user && <InstallPrompt />}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <LanguageProvider>
          <BrowserRouter>
            <Toaster position="top-center" richColors />
            <AppRouter />
          </BrowserRouter>
        </LanguageProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
